import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { formatDate } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Comments (SPEC §56, TASK-703).
 *
 * Lists the discussion mapped to each localization. The comments themselves
 * live in GitHub — this page shows the mapping and links out, rather than
 * mirroring a conversation the CMS does not own.
 *
 * That is also why there is no moderation UI here: deleting someone's comment
 * is an action on GitHub, and pretending otherwise would produce a button that
 * either lies or needs credentials the Admin does not have.
 */
export function Comments() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const data = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listDiscussions(siteId);
  }, [siteId]);

  if (siteLoading || data.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (data.error !== null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-red-600">{data.error}</p>
        </CardContent>
      </Card>
    );
  }

  const discussions = data.data ?? [];

  return (
    <>
      <PageHeader
        title={t("comments.title")}
        description={t("comments.countDesc", { count: discussions.length })}
      />

      {discussions.length === 0 ? (
        <EmptyState
          message={t("comments.noDiscussions")}
          hint={t("comments.emptyHint")}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.locale")}</TableHead>
                <TableHead>{t("comments.thProvider")}</TableHead>
                <TableHead className="text-right">{t("comments.thDiscussionNumber")}</TableHead>
                <TableHead>{t("comments.thMapped")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {discussions.map((discussion) => (
                <TableRow key={discussion.id}>
                  <TableCell>
                    <Badge variant="outline">{discussion.locale}</Badge>
                  </TableCell>
                  <TableCell className="text-neutral-600">{discussion.provider}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {discussion.discussionNumber}
                  </TableCell>
                  <TableCell className="text-neutral-500">
                    {formatDate(discussion.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
